import { CODIGOS_ERRO, type RespostaLogin } from '@baiocchi/shared'

import { ErroApi, erroDeRede, interpretarErro } from './erros'

/**
 * Cliente HTTP único do `apps/web`.
 *
 * Regras que **não** são negociáveis, e o porquê de cada uma:
 *
 * 1. **O token de acesso mora em memória**, nunca em `localStorage`. Guardá-lo lá
 *    entrega a sessão a qualquer XSS; o refresh já é um cookie `httpOnly` que o JS
 *    não lê (RNF-11), e a recarga de página recupera a sessão por `/auth/refresh`.
 *
 * 2. **`credentials: 'include'` em tudo**, para o cookie de refresh chegar em
 *    `/auth/*`. Em desenvolvimento a chamada passa pelo proxy do Vite, então é
 *    mesma origem — sem isso o `SameSite=Lax` não anexaria o cookie no XHR.
 *
 * 3. **`X-CSRF-Token` lido do cookie legível** (double-submit, RNF-37). A API só
 *    exige nas rotas que se autenticam pelo cookie (`/auth/refresh`, `logout`,
 *    `logout-todos`, `alterar-senha`), mas mandar sempre é inofensivo e evita
 *    esquecer uma rota nova.
 *
 * 4. **Um refresh por vez.** Quando o dashboard dispara seis consultas e o token
 *    acabou de vencer, as seis recebem `401`. Sem a promessa compartilhada, seriam
 *    seis chamadas a `/auth/refresh` — e a segunda apresentaria um token já
 *    rotacionado, o que a API trata como reuso e responde revogando a **família
 *    inteira** de sessões (RNF-12). O usuário seria deslogado por causa de um
 *    detalhe do cliente.
 */

const BASE = `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/api/v1`

/** Nome padrão do cookie de CSRF (`CSRF_COOKIE_NOME` do `.env`). */
const COOKIE_CSRF =
  (import.meta.env.VITE_CSRF_COOKIE_NOME as string | undefined) ?? 'baiocchi_csrf'

let tokenAcesso: string | null = null
let refreshEmAndamento: Promise<RespostaLogin | null> | null = null

type OuvinteSessaoPerdida = () => void
const ouvintesSessaoPerdida = new Set<OuvinteSessaoPerdida>()

export function definirTokenAcesso(token: string | null): void {
  tokenAcesso = token
}

export function tokenAtual(): string | null {
  return tokenAcesso
}

/**
 * Avisado quando o refresh falha em definitivo. O contexto de sessão usa para
 * limpar o estado e mandar para o login, em vez de cada tela tratar `401`.
 */
export function aoPerderSessao(ouvinte: OuvinteSessaoPerdida): () => void {
  ouvintesSessaoPerdida.add(ouvinte)
  return () => ouvintesSessaoPerdida.delete(ouvinte)
}

function anunciarSessaoPerdida(): void {
  for (const ouvinte of ouvintesSessaoPerdida) ouvinte()
}

function lerCookie(nome: string): string | null {
  if (typeof document === 'undefined') return null
  const alvo = `${nome}=`
  for (const parte of document.cookie.split(';')) {
    const item = parte.trim()
    if (item.startsWith(alvo)) return decodeURIComponent(item.slice(alvo.length))
  }
  return null
}

export type ParametrosConsulta = Record<
  string,
  string | number | boolean | null | undefined | ReadonlyArray<string>
>

/**
 * Monta a query string do contrato: lista vira `a,b` (§6.5) e valor
 * `null`/`undefined`/`''` é **omitido** — mandar `equipeId=` faria a API recusar
 * com `422 FILTRO_DESCONHECIDO` em vez de ignorar.
 */
export function montarConsulta(parametros?: ParametrosConsulta): string {
  if (!parametros) return ''
  const busca = new URLSearchParams()

  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor === null || valor === undefined || valor === '') continue
    if (Array.isArray(valor)) {
      if (valor.length === 0) continue
      busca.set(chave, valor.join(','))
      continue
    }
    busca.set(chave, String(valor))
  }

  const texto = busca.toString()
  return texto ? `?${texto}` : ''
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  corpo?: unknown
  consulta?: ParametrosConsulta
  /** Rota pública: não tenta refresh nem anuncia sessão perdida no `401`. */
  publica?: boolean
  sinal?: AbortSignal
}

async function executar(caminho: string, opcoes: OpcoesRequisicao): Promise<Response> {
  const cabecalhos = new Headers({ Accept: 'application/json' })

  if (opcoes.corpo !== undefined) cabecalhos.set('Content-Type', 'application/json')
  if (tokenAcesso && !opcoes.publica) cabecalhos.set('Authorization', `Bearer ${tokenAcesso}`)

  const csrf = lerCookie(COOKIE_CSRF)
  if (csrf) cabecalhos.set('X-CSRF-Token', csrf)

  try {
    return await fetch(`${BASE}${caminho}${montarConsulta(opcoes.consulta)}`, {
      method: opcoes.metodo ?? 'GET',
      headers: cabecalhos,
      credentials: 'include',
      body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
      signal: opcoes.sinal,
    })
  } catch (causa) {
    throw erroDeRede(causa)
  }
}

/**
 * Renova o par de tokens. Uma promessa compartilhada por todas as chamadas.
 *
 * Devolve a `RespostaLogin` inteira (e não só o token) porque a abertura da aba
 * usa o mesmo caminho para **restaurar** a sessão: usuário, permissões e token
 * vêm juntos, e uma segunda chamada a `/auth/eu` só para saber quem está logado
 * seria uma ida a mais em cada carregamento.
 */
async function renovarSessao(): Promise<RespostaLogin | null> {
  refreshEmAndamento ??= (async () => {
    try {
      const resposta = await executar('/auth/refresh', { metodo: 'POST', publica: true })
      if (!resposta.ok) return null
      const dados = (await resposta.json()) as RespostaLogin
      if (typeof dados.tokenAcesso !== 'string') return null
      tokenAcesso = dados.tokenAcesso
      return dados
    } catch {
      return null
    } finally {
      // Liberado no próximo tick para que as chamadas que já estavam esperando
      // peguem esta promessa, e não uma nova.
      window.setTimeout(() => {
        refreshEmAndamento = null
      }, 0)
    }
  })()

  return refreshEmAndamento
}

async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  let resposta = await executar(caminho, opcoes)

  if (resposta.status === 401 && !opcoes.publica) {
    const erro = await interpretarErro(resposta.clone())

    // Só tenta renovar quando o motivo é o JWT vencido. `CREDENCIAIS_INVALIDAS`
    // e `REFRESH_REUSADO` não se resolvem com refresh — o segundo, inclusive,
    // já derrubou a família de sessões.
    if (erro.codigo === CODIGOS_ERRO.SESSAO_EXPIRADA) {
      const renovada = await renovarSessao()
      if (renovada) {
        resposta = await executar(caminho, opcoes)
      } else {
        tokenAcesso = null
        anunciarSessaoPerdida()
        throw erro
      }
    } else {
      tokenAcesso = null
      anunciarSessaoPerdida()
      throw erro
    }
  }

  if (!resposta.ok) throw await interpretarErro(resposta)

  if (resposta.status === 204) return undefined as T

  const tipo = resposta.headers.get('content-type') ?? ''
  if (!tipo.includes('application/json')) return (await resposta.text()) as T

  return (await resposta.json()) as T
}

export const api = {
  get: <T>(caminho: string, consulta?: ParametrosConsulta, sinal?: AbortSignal) =>
    requisitar<T>(caminho, { metodo: 'GET', consulta, sinal }),

  post: <T>(caminho: string, corpo?: unknown, consulta?: ParametrosConsulta) =>
    requisitar<T>(caminho, { metodo: 'POST', corpo, consulta }),

  patch: <T>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'PATCH', corpo }),

  put: <T>(caminho: string, corpo?: unknown) => requisitar<T>(caminho, { metodo: 'PUT', corpo }),

  delete: <T>(caminho: string) => requisitar<T>(caminho, { metodo: 'DELETE' }),

  /** Rotas de `/auth` que não têm (nem podem ter) sessão: login, recuperação. */
  publico: <T>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'POST', corpo, publica: true }),

  /** Tenta restaurar a sessão na abertura da aba. `null` quando não há cookie. */
  restaurarSessao: renovarSessao,
}

export { ErroApi }
